import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    VStack,
    HStack,
    Heading,
    Button,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    Input,
    InputGroup,
    InputLeftElement,
    Select,
    Card,
    CardBody,
    CardHeader,
    Badge,
    IconButton,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    useDisclosure,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    FormControl,
    FormLabel,
    FormErrorMessage,
    Spinner,
    Alert,
    AlertIcon,
    useColorModeValue,
    Checkbox,
    Flex,
    Text,
    Tooltip,
    SimpleGrid,
    Stat,
    StatLabel,
    StatNumber,
    StatHelpText,
    StatArrow,
    Switch,
    ButtonGroup,
    Divider
} from '@chakra-ui/react';
import {
    MdSearch,
    MdAdd,
    MdEdit,
    MdDelete,
    MdMoreVert,
    MdRefresh,
    MdDownload,
    MdSettings,
    MdPowerSettingsNew,
    MdBuild,
    MdOnlinePrediction,
    MdOfflineBolt,
    MdWarning,
    MdCheckCircle,
    MdError,
    MdInfo
} from 'react-icons/md';
import * as XLSX from 'xlsx';
import apiService from '../services/apiService';
import { formatDateTime, getStatusColor, formatNumber, getRelativeTime } from '../utils/helpers';
import { ToastManager } from '../components/Common/ToastManager';
import usageTracker from '../services/usageTracker';

/**
 * Client Management Page Component
 */
function Client({ userProfile, appConfig }) {
    // State management - exactly matching backend response structure
    const [clients, setClients] = useState([]);
    const [filteredClients, setFilteredClients] = useState([]);
    const [statistics, setStatistics] = useState([]);
    const [loading, setLoading] = useState(true);
    const [operationLoading, setOperationLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [selectedClients, setSelectedClients] = useState([]);
    const [currentClient, setCurrentClient] = useState(null);
    const [formErrors, setFormErrors] = useState({});

    // Pagination state - matching backend PageResponse structure
    const [pagination, setPagination] = useState({
        page: 1,
        size: 20,
        total: 0,
        totalPages: 0,
        hasNext: false,
        hasPrevious: false
    });

    // Modal states for different operations
    const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
    const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
    const { isOpen: isBatchOpen, onOpen: onBatchOpen, onClose: onBatchClose } = useDisclosure();
    const { isOpen: isStatsOpen, onOpen: onStatsOpen, onClose: onStatsClose } = useDisclosure();

    // Form state - matching backend ClientRequest structure
    const [formData, setFormData] = useState({
        clientName: '',
        clientSecret: '',
        clientStatus: 'Offline',
        clientReportPerson: '',
        prismVersion: '',
        baseUrl: '',
        sendChecklistAlerts: false,
        emtDelayEnabled: true,
        isInternal: false,
        redmineProjectId: '',
        marketDataClient1: '',
        marketDataClient2: '',
    });

    // Batch operation state
    const [batchOperation, setBatchOperation] = useState({
        operation: 'status',
        status: 'Offline'
    });

    // Theme colors for consistent UI
    const cardBg = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const textColor = useColorModeValue('gray.600', 'gray.300');
    const headingColor = useColorModeValue('gray.800', 'white');
    const statsBg = useColorModeValue('gray.50', 'gray.700');

    /**
     * Initialize component and load data
     * Loads clients and statistics on mount
     */
    useEffect(() => {
        initializeData();
    }, []);

    /**
     * Reload clients when pagination changes
     * Triggers on any page or size change
     */
    useEffect(() => {
        loadClients();
    }, [pagination.page, pagination.size, searchTerm, statusFilter]);

    /**
     * Filter clients when search or status filter changes
     * Implements client-side filtering for better UX
     */
    useEffect(() => {
        filterClients();
    }, [clients, searchTerm, statusFilter]);

    /**
     * Initialize data loading
     * Loads both clients and statistics concurrently
     */
    const initializeData = async () => {
        await Promise.all([
            loadStatistics()
        ]);
    };

    /**
     * Load clients with exact backend API call structure
     * Handles PageResponse structure from backend properly
     */
    const loadClients = async () => {
        try {
            setLoading(true);

            // Build request parameters
            const requestParams = {
                page: pagination.page,
                size: pagination.size,
                sortBy: 'updated_at_utc',
                sortDirection: 'DESC'
            };

            // Add search if present
            if (searchTerm && searchTerm.trim()) {
                requestParams.search = searchTerm.trim();
            }

            const response = await apiService.getClientsList(requestParams);

            if (response.success) {
                // Handle exact backend response structure
                const responseData = response.data;
                
                // Backend returns PageResponse<ClientResponse>
                const clientsData = responseData.content || [];
                
                // Map to ensure all fields are properly handled
                const mappedClients = clientsData.map(client => ({
                    clientId: client.clientId,
                    clientCode: client.clientCode,
                    clientName: client.clientName,
                    clientStatus: client.clientStatus,
                    hasRefreshToken: client.hasRefreshToken,
                    lastHeartbeat: client.lastHeartbeat,
                    connectionDuration: client.connectionDuration,
                    createdAtUtc: client.createdAtUtc,
                    updatedAtUtc: client.updatedAtUtc,
                    deleted: client.deleted || false,
                    version: client.version,
                    reportPerson:client.reportPerson,
                    prismVersion:client.prismVersion,
                    baseUrl: client.baseUrl,
                    sendChecklistAlerts: client.sendChecklistAlerts || 0,
                    emtDelayEnabled: client.emtDelayEnabled ?? 1,
                    isInternal: client.isInternal ?? 0,
                    redmineProjectId: client.redmineProjectId || '',
                    marketDataClient1: client.marketDataClient1 || '',
                    marketDataClient2: client.marketDataClient2 || ''
                }));

                setClients(mappedClients);

                // Handle pagination from backend PageResponse properly
                if (responseData.pagination) {
                    setPagination(prev => ({
                        ...prev,
                        total: responseData.pagination.total || 0,
                        totalPages: responseData.pagination.totalPages || 0,
                        hasNext: responseData.pagination.hasNext || false,
                        hasPrevious: responseData.pagination.hasPrevious || false
                    }));
                } else {
                    // Fallback for non-paginated response
                    setPagination(prev => ({
                        ...prev,
                        total: mappedClients.length,
                        totalPages: 1,
                        hasNext: false,
                        hasPrevious: false
                    }));
                }
            } else {
                throw new Error(response.message || 'Failed to load clients');
            }
        } catch (error) {
            ToastManager.showError(`Failed to load clients: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Load all clients data for Excel export
     * Fetches complete dataset across all pages with current search/filter criteria
     */
    const loadAllClientsForExport = async () => {
        try {
            // Build request parameters to get all data
            const requestParams = {
                page: 1,
                size: pagination.total > 0 ? pagination.total : 10000, // Use total count or large number
                sortBy: 'updated_at_utc',
                sortDirection: 'DESC'
            };

            // Add search if present to maintain filter consistency
            if (searchTerm && searchTerm.trim()) {
                requestParams.search = searchTerm.trim();
            }

            // Add status filter if not 'all'
            if (statusFilter !== 'all') {
                // For status-specific data, use status endpoint for better performance
                const response = await apiService.getClientsByStatus(statusFilter, requestParams);
                if (response.success) {
                    return response.data.content || response.data || [];
                }
                throw new Error(response.message || 'Failed to load clients by status');
            } else {
                // For all clients, use general list endpoint
                const response = await apiService.getClientsList(requestParams);
                if (response.success) {
                    const responseData = response.data;
                    return responseData.content || responseData || [];
                }
                throw new Error(response.message || 'Failed to load all clients');
            }
        } catch (error) {
            throw new Error(`Failed to fetch complete client data: ${error.message}`);
        }
    };

    /**
     * Load client statistics from backend
     * Matches GET /cas/client/statistics endpoint
     */
    const loadStatistics = async () => {
        try {
            const response = await apiService.getClientStatistics();

            if (response.success) {
                setStatistics(response.data || []);
            }
        } catch (error) {
            // Statistics failure is not critical for main functionality
        }
    };

    /**
     * Filter clients based on search term and status
     * Implements client-side filtering with debouncing
     */
    const filterClients = useCallback(() => {
        let filtered = [...clients];

        // Apply status filter if not using server-side filtering
        if (statusFilter !== 'all' && !searchTerm) {
            filtered = filtered.filter(client => 
                client.clientStatus?.toLowerCase() === statusFilter.toLowerCase()
            );
        }

        setFilteredClients(filtered);
    }, [clients, statusFilter, searchTerm]);

    /**
     * Handle search term changes with debouncing
     * Resets pagination to page 1 when search changes
     */
    const handleSearchChange = (value) => {
        setSearchTerm(value);
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    /**
     * Handle status filter changes
     * Resets pagination to page 1 when filter changes
     */
    const handleStatusFilterChange = (value) => {
        setStatusFilter(value);
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    /**
     * Validate form data before submission
     * Matches backend validation requirements
     */
    const validateForm = (data, isEdit = false) => {
        const errors = {};

        // Client name validation - required, length 1-255
        if (!data.clientName?.trim()) {
            errors.clientName = 'Client name is required';
        } else if (data.clientName.length > 255) {
            errors.clientName = 'Client name must be less than 255 characters';
        }

        // Client secret validation - required for create, optional for edit, length 8-1000
        if (!isEdit) {
            if (!data.clientSecret?.trim()) {
                errors.clientSecret = 'Client secret is required';
            } else if (data.clientSecret.length < 8) {
                errors.clientSecret = 'Client secret must be at least 8 characters';
            } else if (data.clientSecret.length > 1000) {
                errors.clientSecret = 'Client secret must be less than 1000 characters';
            }
        } else if (data.clientSecret && data.clientSecret.length > 0) {
            if (data.clientSecret.length < 8) {
                errors.clientSecret = 'Client secret must be at least 8 characters';
            } else if (data.clientSecret.length > 1000) {
                errors.clientSecret = 'Client secret must be less than 1000 characters';
            }
        }
        // Prism version validation - required, semantic version format only (e.g., 5.6, 6.1, 5.6.2)
        if (!data.prismVersion?.trim()) {
            errors.prismVersion = 'Prism version is required';
        } else {
            const versionPattern = /^\d+\.\d+(\.\d+)?$/;
            if (!versionPattern.test(data.prismVersion.trim())) {
                errors.prismVersion = 'Prism version must be in format X.Y or X.Y.Z (e.g., 5.6, 6.1, 5.6.2)';
            } else if (data.prismVersion.length > 20) {
                errors.prismVersion = 'Prism version must be less than 20 characters';
            }
        }

        // Status validation
        const validStatuses = ['Online', 'Offline', 'Maintenance'];
        if (!validStatuses.includes(data.clientStatus)) {
            errors.clientStatus = 'Invalid client status';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    /**
     * Handle client creation - exactly matching backend endpoint
     * Matches POST /cas/client with admin role validation
     */
    const handleCreateClient = async () => {
        try {
            if (!validateForm(formData)) {
                ToastManager.showError('Please fix validation errors');
                return;
            }

            setOperationLoading(true);

            const response = await apiService.createClient(formData);

            if (response.success) {
                usageTracker.action('Clients', 'CREATE');
                ToastManager.showSuccess(response.message || 'Client created successfully');
                onCreateClose();
                resetForm();
                loadClients();
                loadStatistics(); // Refresh statistics
            } else {
                throw new Error(response.message || 'Failed to create client');
            }
        } catch (error) {
            ToastManager.showError(`Failed to create client: ${error.message}`);
        } finally {
            setOperationLoading(false);
        }
    };

    /**
     * Handle client update - exactly matching backend endpoint
     * Matches PUT /cas/client/{id} with admin role validation
     */
    const handleUpdateClient = async () => {
        try {
            if (!currentClient?.clientId) return;
            if (!validateForm(formData, true)) {
                ToastManager.showError('Please fix validation errors');
                return;
            }

            setOperationLoading(true);

            const updateData = {
                clientName: formData.clientName,
                clientStatus: formData.clientStatus,
                clientReportPerson:formData.clientReportPerson,
                prismVersion:formData.prismVersion,
                baseUrl:formData.baseUrl,
                sendChecklistAlerts: formData.sendChecklistAlerts,
                emtDelayEnabled: formData.emtDelayEnabled,
                isInternal: formData.isInternal,
                redmineProjectId: formData.redmineProjectId,
                marketDataClient1: formData.marketDataClient1,
                marketDataClient2: formData.marketDataClient2,
            };

            // Only include client secret if provided
            if (formData.clientSecret?.trim()) {
                updateData.clientSecret = formData.clientSecret;
            }

            const response = await apiService.updateClient(currentClient.clientId, updateData);

            if (response.success) {
                usageTracker.action('Clients', 'UPDATE');
                ToastManager.showSuccess('Client updated successfully');
                onEditClose();
                resetForm();
                loadClients();
            } else {
                throw new Error(response.message || 'Failed to update client');
            }
        } catch (error) {
            ToastManager.showError(`Failed to update client: ${error.message}`);
        } finally {
            setOperationLoading(false);
        }
    };

    /**
     * Handle client deletion - exactly matching backend endpoint
     * Matches DELETE /cas/client/{id} with admin role validation
     */
    const handleDeleteClient = async () => {
        try {
            if (!currentClient?.clientId) return;

            setOperationLoading(true);

            const response = await apiService.deleteClient(currentClient.clientId);

            if (response.success) {
                usageTracker.action('Clients', 'DELETE');
                ToastManager.showSuccess(response.message || 'Client deleted successfully');
                onDeleteClose();
                setCurrentClient(null);
                loadClients();
                loadStatistics(); // Refresh statistics
            } else {
                throw new Error(response.message || 'Failed to delete client');
            }
        } catch (error) {
            ToastManager.showError(`Failed to delete client: ${error.message}`);
        } finally {
            setOperationLoading(false);
        }
    };

    /**
     * Handle batch operations - exactly matching backend endpoint
     * Matches POST /cas/client/batch/status with admin role validation
     */
    const handleBatchOperation = async () => {
        try {
            if (selectedClients.length === 0) {
                ToastManager.showError('Please select clients for batch operation');
                return;
            }

            setOperationLoading(true);

            let response;
            if (batchOperation.operation === 'status') {
                response = await apiService.batchUpdateClientStatus(selectedClients, batchOperation.status);
            } else if (batchOperation.operation === 'delete') {
                // Batch delete - call individual delete APIs
                for (const clientId of selectedClients) {
                    await apiService.deleteClient(clientId);
                }
                response = { success: true, message: `${selectedClients.length} clients deleted successfully` };
            }

            if (response.success) {
                usageTracker.action('Clients',
                    batchOperation.operation === 'delete' ? 'BATCH_DELETE' : 'BATCH_STATUS_UPDATE');
                ToastManager.showSuccess(response.message || 'Batch operation completed successfully');
                onBatchClose();
                setSelectedClients([]);
                loadClients();
                loadStatistics(); // Refresh statistics
            } else {
                throw new Error(response.message || 'Batch operation failed');
            }
        } catch (error) {
            ToastManager.showError(`Batch operation failed: ${error.message}`);
        } finally {
            setOperationLoading(false);
        }
    };

    /**
     * Handle status toggle operations
     * Implements quick status changes for individual clients
     */
    const handleStatusToggle = async (client, newStatus) => {
        try {
            setOperationLoading(true);

            const response = await apiService.updateClientStatus(client.clientId, newStatus);

            if (response.success) {
                usageTracker.action('Clients', 'STATUS_CHANGE');
                ToastManager.showSuccess(`Client status updated to ${newStatus}`);
                loadClients();
            } else {
                throw new Error(response.message || 'Failed to update status');
            }
        } catch (error) {
            ToastManager.showError(`Failed to update status: ${error.message}`);
        } finally {
            setOperationLoading(false);
        }
    };

    /**
     * Handle maintenance mode toggle
     * Matches PUT /cas/client/{id}/maintenance endpoint
     */
    const handleMaintenanceToggle = async (client, enable) => {
        try {
            setOperationLoading(true);
            const action = enable ? 'enable' : 'disable';

            const response = enable ? 
                await apiService.enableMaintenanceMode(client.clientId) :
                await apiService.disableMaintenanceMode(client.clientId);

            if (response.success) {
                usageTracker.action('Clients', enable ? 'ENABLE_MAINTENANCE' : 'DISABLE_MAINTENANCE');
                ToastManager.showSuccess(`Maintenance mode ${action}d successfully`);
                loadClients();
            } else {
                throw new Error(response.message || `Failed to ${action} maintenance mode`);
            }
        } catch (error) {
            ToastManager.showError(`Failed to toggle maintenance mode: ${error.message}`);
        } finally {
            setOperationLoading(false);
        }
    };

    /**
     * Open edit modal with client data
     * Prepares form for editing existing client
     */
    const openEditModal = (client) => {
        setCurrentClient(client);
        setFormData({
            clientName: client.clientName || '',
            clientSecret: '', // Don't populate secret for security
            clientStatus: client.clientStatus || 'Offline',
            clientReportPerson:client.reportPerson|| '',
            prismVersion:client.prismVersion||'',
            baseUrl:client.baseUrl|| '',
            sendChecklistAlerts: client.sendChecklistAlerts || false,
            emtDelayEnabled: (client.emtDelayEnabled ?? 1) === 1,
            isInternal: (client.isInternal ?? 0) === 1,
            redmineProjectId: client.redmineProjectId || '',
            marketDataClient1: client.marketDataClient1 || '',
            marketDataClient2: client.marketDataClient2 || '',
        });
        setFormErrors({});
        onEditOpen();
    };

    /**
     * Open delete confirmation modal
     * Prepares deletion confirmation with client details
     */
    const openDeleteModal = (client) => {
        setCurrentClient(client);
        onDeleteOpen();
    };

    /**
     * Open batch operations modal
     * Prepares batch operation interface
     */
    const openBatchModal = () => {
        if (selectedClients.length === 0) {
            ToastManager.showError('Please select clients for batch operations');
            return;
        }
        setBatchOperation({ operation: 'status', status: 'Offline' });
        onBatchOpen();
    };

    /**
     * Reset form data and errors
     * Clears all form fields and validation errors
     */
    const resetForm = () => {
        setFormData({
            clientName: '',
            clientSecret: '',
            clientStatus: 'Offline',
            clientReportPerson: '',
            prismVersion:'',
            baseUrl:'',
            sendChecklistAlerts: false,
            emtDelayEnabled: true,
            isInternal: false,
            redmineProjectId: '',
            marketDataClient1: '',
            marketDataClient2: '',
        });
        setFormErrors({});
        setCurrentClient(null);
    };

    /**
     * Handle client selection for batch operations
     * Manages checkbox selection state
     */
    const handleClientSelection = (clientId, isSelected) => {
        if (isSelected) {
            setSelectedClients(prev => [...prev, clientId]);
        } else {
            setSelectedClients(prev => prev.filter(id => id !== clientId));
        }
    };

    /**
     * Handle select all clients toggle
     * Selects or deselects all visible clients
     */
    const handleSelectAll = (isSelected) => {
        if (isSelected) {
            setSelectedClients(filteredClients.map(client => client.clientId));
        } else {
            setSelectedClients([]);
        }
    };

    /**
     * Export ALL clients to Excel across all pages
     * Downloads complete client data in Excel format with proper formatting
     * Optimized to fetch all data before export regardless of current pagination
     */
    const handleExportClients = async () => {
        try {
            setExportLoading(true);

            // Step 1: Fetch all client data across all pages
            const allClientsData = await loadAllClientsForExport();

            if (!allClientsData || allClientsData.length === 0) {
                ToastManager.showError('No client data available for export');
                return;
            }

            // Step 2: Prepare data for Excel export with complete dataset
            const excelData = allClientsData.map(client => ({
                'Client ID': client.clientId,
                'Client Name': client.clientName,
                'Client Code': client.clientCode,
                'Status': client.clientStatus,
                'Has Refresh Token': client.hasRefreshToken ? 'Yes' : 'No',
                'Last Heartbeat': client.lastHeartbeat ? formatDateTime(client.lastHeartbeat) : 'Never',
                'Connection Duration': client.connectionDuration || 'N/A',
                'Created Date': formatDateTime(client.createdAtUtc),
                'Updated Date': formatDateTime(client.updatedAtUtc),
                'Deleted': client.deleted ? 'Yes' : 'No',
                'Version': client.version || 1
            }));

            // Step 3: Create workbook and worksheet
            const workbook = XLSX.utils.book_new();
            const worksheet = XLSX.utils.json_to_sheet(excelData);

            // Step 4: Set column widths for better readability
            const columnWidths = [
                { wch: 12 }, // Client ID
                { wch: 25 }, // Client Name
                { wch: 40 }, // Client Code
                { wch: 12 }, // Status
                { wch: 18 }, // Has Refresh Token
                { wch: 20 }, // Last Heartbeat
                { wch: 20 }, // Connection Duration
                { wch: 20 }, // Created Date
                { wch: 20 }, // Updated Date
                { wch: 10 }, // Deleted
                { wch: 10 }  // Version
            ];
            worksheet['!cols'] = columnWidths;

            // Step 5: Add worksheet to workbook
            XLSX.utils.book_append_sheet(workbook, worksheet, 'All Clients');

            // Step 6: Add metadata worksheet with comprehensive export information
            const metadataSheet = XLSX.utils.json_to_sheet([
                { Property: 'Export Date', Value: new Date().toISOString() },
                { Property: 'Total Records Exported', Value: allClientsData.length },
                { Property: 'Current Page Records', Value: filteredClients.length },
                { Property: 'Total Database Records', Value: pagination.total },
                { Property: 'Search Term Applied', Value: searchTerm || 'None' },
                { Property: 'Status Filter Applied', Value: statusFilter === 'all' ? 'All Statuses' : statusFilter },
                { Property: 'Export Scope', Value: 'Complete Dataset (All Pages)' },
                { Property: 'Exported By', Value: userProfile?.email || 'System Administrator' },
                { Property: 'Application', Value: 'Client Management System' },
                { Property: 'System Version', Value: '1.0.0' }
            ]);
            metadataSheet['!cols'] = [{ wch: 25 }, { wch: 35 }];
            XLSX.utils.book_append_sheet(workbook, metadataSheet, 'Export Metadata');

            // Step 7: Add summary statistics worksheet
            const statusCounts = allClientsData.reduce((acc, client) => {
                acc[client.clientStatus] = (acc[client.clientStatus] || 0) + 1;
                return acc;
            }, {});

            const summaryData = [
                { Metric: 'Total Clients', Value: allClientsData.length },
                { Metric: 'Online Clients', Value: statusCounts['Online'] || 0 },
                { Metric: 'Offline Clients', Value: statusCounts['Offline'] || 0 },
                { Metric: 'Maintenance Clients', Value: statusCounts['Maintenance'] || 0 },
                { Metric: 'Clients with Refresh Token', Value: allClientsData.filter(c => c.hasRefreshToken).length },
                { Metric: 'Recently Active (Last 24h)', Value: allClientsData.filter(c => 
                    c.lastHeartbeat && new Date(c.lastHeartbeat) > new Date(Date.now() - 24 * 60 * 60 * 1000)
                ).length },
                { Metric: 'Never Connected', Value: allClientsData.filter(c => !c.lastHeartbeat).length }
            ];

            const summarySheet = XLSX.utils.json_to_sheet(summaryData);
            summarySheet['!cols'] = [{ wch: 25 }, { wch: 15 }];
            XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary Statistics');

            // Step 8: Generate filename with current date and scope indicator
            const filename = `clients_complete_export_${new Date().toISOString().split('T')[0]}.xlsx`;

            // Step 9: Write and download the file
            XLSX.writeFile(workbook, filename);
            usageTracker.action('Clients', 'EXPORT_EXCEL');

            ToastManager.showSuccess(
                `Complete client database exported successfully! 
                ${allClientsData.length} records exported to ${filename}`
            );

        } catch (error) {
            ToastManager.showError(`Failed to export complete client data: ${error.message}`);
        } finally {
            setExportLoading(false);
        }
    };

    /**
     * Handle pagination change
     * Updates page and reloads data automatically via useEffect
     */
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            setPagination(prev => ({ ...prev, page: newPage }));
        }
    };

    /**
     * Handle page size change
     * Updates size and resets to page 1
     */
    const handlePageSizeChange = (newSize) => {
        setPagination(prev => ({ 
            ...prev, 
            page: 1,  // Reset to first page
            size: parseInt(newSize, 10) 
        }));
    };

    /**
     * Get status badge color based on client status
     * Provides consistent status visualization
     */
    const getStatusBadgeProps = (status) => {
        switch (status) {
            case 'Online':
                return { colorScheme: 'green', icon: MdCheckCircle };
            case 'Offline':
                return { colorScheme: 'red', icon: MdError };
            case 'Maintenance':
                return { colorScheme: 'orange', icon: MdWarning };
            default:
                return { colorScheme: 'gray', icon: MdInfo };
        }
    };

    /**
     * Get connection info display
     * Shows user-friendly connection status
     */
    const getConnectionInfo = (client) => {
        if (client.clientStatus === 'Online') {
            return client.connectionDuration || 'Connected recently';
        } else if (client.lastHeartbeat) {
            return `Last seen ${getRelativeTime(client.lastHeartbeat)}`;
        } else {
            return 'Never connected';
        }
    };

    return (
        <Box p={6}>
            <VStack spacing={6} align="stretch">
                {/* Page Header with Statistics */}
                <Flex justify="space-between" align="flex-start" wrap="wrap" gap={4}>
                    <Box>
                        <Heading size="lg" color={headingColor} mb={2}>
                            Client Management
                        </Heading>
                        <Text color={textColor}>
                            Manage DCN clients and monitor connection status
                        </Text>
                    </Box>
                    <HStack spacing={3} wrap="wrap">
                        <Button
                            leftIcon={<MdRefresh />}
                            onClick={initializeData}
                            variant="outline"
                            size="sm"
                            isLoading={loading}
                        >
                            Refresh
                        </Button>
                        <Tooltip label="Export complete client database to Excel (.xlsx) - All pages included" hasArrow placement="bottom">
                            <Button
                                leftIcon={<MdDownload />}
                                onClick={handleExportClients}
                                variant="outline"
                                size="sm"
                                isLoading={exportLoading}
                                loadingText="Exporting..."
                                isDisabled={pagination.total === 0}
                            >
                                Export All Data
                            </Button>
                        </Tooltip>
                        {statistics.length > 0 && (
                            <Button
                                leftIcon={<MdInfo />}
                                onClick={onStatsOpen}
                                variant="outline"
                                size="sm"
                            >
                                Statistics
                            </Button>
                        )}
                        <Button
                            leftIcon={<MdAdd />}
                            onClick={() => {
                                resetForm();
                                onCreateOpen();
                            }}
                            colorScheme="brand"
                            size="sm"
                        >
                            Add Client
                        </Button>
                    </HStack>
                </Flex>

                {/* Quick Statistics Cards */}
                {statistics.length > 0 && (
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
                        {statistics.map((stat, index) => (
                              <Card key={index} bg={cardBg} borderColor={borderColor} size="sm">
                                <CardBody>
                                    <Stat>
                                        <StatLabel fontSize="sm" color={textColor}>
                                            {stat.label || stat.name}
                                        </StatLabel>
                                        <StatNumber fontSize="2xl" color={headingColor}>
                                            {formatNumber(stat.value || stat.count)}
                                        </StatNumber>
                                         {/* Hide StatHelpText for total clients */}
                                         {!(stat.key === 'total' || (stat.label || stat.name).toLowerCase().includes('total')) && stat.percentage && (
                                            <StatHelpText>
                                                <StatArrow type={stat.percentage > 0 ? 'increase' : 'decrease'} />
                                                {Math.abs(stat.percentage)}%
                                            </StatHelpText>
                                        )}
                                    </Stat>
                                </CardBody>
                            </Card>
                        ))}
                    </SimpleGrid>
                )}

                {/* Filters and Search */}
                <Card bg={cardBg} borderColor={borderColor}>
                    <CardBody>
                        <Flex wrap="wrap" gap={4} align="center" justify="space-between">
                            <HStack spacing={4} wrap="wrap" flex="1">
                                <InputGroup maxW="300px">
                                    <InputLeftElement>
                                        <MdSearch color={textColor} />
                                    </InputLeftElement>
                                    <Input
                                        placeholder="Search clients..."
                                        value={searchTerm}
                                        onChange={(e) => handleSearchChange(e.target.value)}
                                    />
                                </InputGroup>
                                
                                <Select
                                    value={statusFilter}
                                    onChange={(e) => handleStatusFilterChange(e.target.value)}
                                    maxW="200px"
                                >
                                    <option value="all">All Status</option>
                                    <option value="Online">Online</option>
                                    <option value="Offline">Offline</option>
                                    <option value="Maintenance">Maintenance</option>
                                </Select>

                                <Select
                                    value={pagination.size}
                                    onChange={(e) => handlePageSizeChange(e.target.value)}
                                    maxW="120px"
                                    size="sm"
                                >
                                    <option value={10}>10 per page</option>
                                    <option value={20}>20 per page</option>
                                    <option value={50}>50 per page</option>
                                    <option value={100}>100 per page</option>
                                </Select>
                            </HStack>

                            {selectedClients.length > 0 && (
                                <ButtonGroup size="sm" spacing={2}>
                                    <Button
                                        leftIcon={<MdSettings />}
                                        onClick={openBatchModal}
                                        colorScheme="blue"
                                        variant="outline"
                                    >
                                        Batch Actions ({selectedClients.length})
                                    </Button>
                                    <Button
                                        leftIcon={<MdDelete />}
                                        onClick={() => {
                                            setBatchOperation({ operation: 'delete' });
                                            onBatchOpen();
                                        }}
                                        colorScheme="red"
                                        variant="outline"
                                    >
                                        Delete Selected
                                    </Button>
                                </ButtonGroup>
                            )}
                        </Flex>
                    </CardBody>
                </Card>

                {/* Clients Table */}
                <Card bg={cardBg} borderColor={borderColor}>
                    <CardHeader>
                        <HStack justify="space-between" wrap="wrap">
                            <Heading size="md" color={headingColor}>
                                Clients ({formatNumber(pagination.total)})
                            </Heading>
                            <HStack spacing={4}>
                                <Badge colorScheme="blue" variant="subtle">
                                    Page {pagination.page} of {pagination.totalPages || 1}
                                </Badge>
                                <Badge colorScheme="green" variant="subtle">
                                    Showing {filteredClients.length} of {formatNumber(pagination.total)}
                                </Badge>
                            </HStack>
                        </HStack>
                    </CardHeader>
                    <CardBody p={0}>
                        {loading ? (
                            <Flex justify="center" p={8}>
                                <VStack spacing={4}>
                                    <Spinner size="lg" color="brand.500" thickness="3px" />
                                    <Text color={textColor}>Loading clients...</Text>
                                </VStack>
                            </Flex>
                        ) : filteredClients.length === 0 ? (
                            <Flex justify="center" p={8}>
                                <VStack spacing={4}>
                                    <Text fontSize="lg" color={textColor}>No clients found</Text>
                                    <Text fontSize="sm" color={textColor} opacity={0.7}>
                                        {searchTerm || statusFilter !== 'all' ? 
                                            'Try adjusting your search or filter criteria' :
                                            'Create your first client to get started'
                                        }
                                    </Text>
                                </VStack>
                            </Flex>
                        ) : (
                            <>
                                <TableContainer>
                                    <Table variant="simple" size="sm">
                                        <Thead>
                                            <Tr>
                                                <Th w="50px">
                                                    <Checkbox
                                                        isChecked={selectedClients.length === filteredClients.length && filteredClients.length > 0}
                                                        isIndeterminate={selectedClients.length > 0 && selectedClients.length < filteredClients.length}
                                                        onChange={(e) => handleSelectAll(e.target.checked)}
                                                    />
                                                </Th>
                                                <Th>Client Details</Th>
                                                <Th>Status</Th>
                                                <Th>Updated At</Th>
                                                <Th>Security</Th>
                                                <Th>Actions</Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {filteredClients.map((client) => {
                                                const statusProps = getStatusBadgeProps(client.clientStatus);
                                                return (
                                                    <Tr key={client.clientId}>
                                                        <Td>
                                                            <Checkbox
                                                                isChecked={selectedClients.includes(client.clientId)}
                                                                onChange={(e) => handleClientSelection(client.clientId, e.target.checked)}
                                                            />
                                                        </Td>
                                                        <Td>
                                                            <VStack align="start" spacing={1}>
                                                                <Text fontWeight="semibold" fontSize="sm">
                                                                    {client.clientName}
                                                                </Text>
                                                                <Tooltip label={client.clientCode} hasArrow>
                                                                    <Text fontSize="xs" color={textColor} fontFamily="mono">
                                                                        {client.clientCode?.substring(0, 8)}...
                                                                    </Text>
                                                                </Tooltip>
                                                            </VStack>
                                                        </Td>
                                                        <Td>
                                                            <HStack spacing={2}>
                                                                <Badge
                                                                    colorScheme={statusProps.colorScheme}
                                                                    variant="subtle"
                                                                    display="flex"
                                                                    alignItems="center"
                                                                    gap={1}
                                                                >
                                                                    <Box as={statusProps.icon} w="3" h="3" />
                                                                    {client.clientStatus}
                                                                </Badge>
                                                                <Switch
                                                                    size="sm"
                                                                    isChecked={client.clientStatus === 'Maintenance'}
                                                                    onChange={(e) => handleMaintenanceToggle(client, e.target.checked)}
                                                                    isDisabled={operationLoading}
                                                                    colorScheme="orange"
                                                                />
                                                            </HStack>
                                                        </Td>
                                                        <Td>
                                                            <VStack align="start" spacing={1}>
                                                                <Text fontSize="xs" color={textColor}>
                                                                    {getConnectionInfo(client)}
                                                                </Text>
                                                                {client.lastHeartbeat && (
                                                                    <Text fontSize="xs" color={textColor} opacity={0.7}>
                                                                        {formatDateTime(client.lastHeartbeat)}
                                                                    </Text>
                                                                )}
                                                            </VStack>
                                                        </Td>
                                                        <Td>
                                                            <HStack spacing={2}>
                                                                <Badge
                                                                    colorScheme={client.hasRefreshToken ? 'green' : 'gray'}
                                                                    variant="outline"
                                                                    fontSize="xs"
                                                                >
                                                                    {client.hasRefreshToken ? 'Authenticated' : 'No Token'}
                                                                </Badge>
                                                            </HStack>
                                                        </Td>
                                                        <Td>
                                                            <HStack spacing={1}>
                                                                <IconButton
                                                                    icon={client.clientStatus === 'Online' ? <MdOfflineBolt /> : <MdOnlinePrediction />}
                                                                    size="sm"
                                                                    variant="ghost"
                                                                    colorScheme={client.clientStatus === 'Online' ? 'red' : 'green'}
                                                                    title={client.clientStatus === 'Online' ? 'Set Offline' : 'Set Online'}
                                                                    onClick={() => handleStatusToggle(client, client.clientStatus === 'Online' ? 'Offline' : 'Online')}
                                                                    isDisabled={operationLoading}
                                                                />
                                                                <Menu>
                                                                    <MenuButton
                                                                        as={IconButton}
                                                                        icon={<MdMoreVert />}
                                                                        size="sm"
                                                                        variant="ghost"
                                                                    />
                                                                    <MenuList>
                                                                        <MenuItem
                                                                            icon={<MdEdit />}
                                                                            onClick={() => openEditModal(client)}
                                                                        >
                                                                            Edit Client
                                                                        </MenuItem>
                                                                        <MenuItem
                                                                            icon={<MdBuild />}
                                                                            onClick={() => handleMaintenanceToggle(client, !client.clientStatus === 'Maintenance')}
                                                                        >
                                                                            {client.clientStatus === 'Maintenance' ? 'Exit' : 'Enter'} Maintenance
                                                                        </MenuItem>
                                                                        <Divider />
                                                                        <MenuItem
                                                                            icon={<MdDelete />}
                                                                            onClick={() => openDeleteModal(client)}
                                                                            color="red.500"
                                                                        >
                                                                            Delete Client
                                                                        </MenuItem>
                                                                    </MenuList>
                                                                </Menu>
                                                            </HStack>
                                                        </Td>
                                                    </Tr>
                                                );
                                            })}
                                        </Tbody>
                                    </Table>
                                </TableContainer>

                                {/* Enhanced Pagination Controls */}
                                {pagination.totalPages > 1 && (
                                    <Flex justify="space-between" align="center" p={4} borderTop="1px" borderTopColor={borderColor}>
                                        <Text fontSize="sm" color={textColor}>
                                            Showing {((pagination.page - 1) * pagination.size) + 1} to {Math.min(pagination.page * pagination.size, pagination.total)} of {pagination.total} entries
                                        </Text>
                                        
                                        <HStack spacing={2}>
                                            <Button
                                                size="sm"
                                                onClick={() => handlePageChange(1)}
                                                isDisabled={!pagination.hasPrevious}
                                                variant="outline"
                                            >
                                                First
                                            </Button>
                                            <Button
                                                size="sm"
                                                onClick={() => handlePageChange(pagination.page - 1)}
                                                isDisabled={!pagination.hasPrevious}
                                                variant="outline"
                                            >
                                                Previous
                                            </Button>
                                            
                                            {/* Page Numbers */}
                                            {(() => {
                                                const pages = [];
                                                const maxVisible = 5;
                                                let startPage = Math.max(1, pagination.page - 2);
                                                let endPage = Math.min(pagination.totalPages, startPage + maxVisible - 1);
                                                
                                                if (endPage - startPage < maxVisible - 1) {
                                                    startPage = Math.max(1, endPage - maxVisible + 1);
                                                }
                                                
                                                for (let i = startPage; i <= endPage; i++) {
                                                    pages.push(
                                                        <Button
                                                            key={i}
                                                            size="sm"
                                                            variant={i === pagination.page ? "solid" : "outline"}
                                                            colorScheme={i === pagination.page ? "brand" : "gray"}
                                                            onClick={() => handlePageChange(i)}
                                                        >
                                                            {i}
                                                        </Button>
                                                    );
                                                }
                                                return pages;
                                            })()}
                                            
                                            <Button
                                                size="sm"
                                                onClick={() => handlePageChange(pagination.page + 1)}
                                                isDisabled={!pagination.hasNext}
                                                variant="outline"
                                            >
                                                Next
                                            </Button>
                                            <Button
                                                size="sm"
                                                onClick={() => handlePageChange(pagination.totalPages)}
                                                isDisabled={!pagination.hasNext}
                                                variant="outline"
                                            >
                                                Last
                                            </Button>
                                        </HStack>
                                    </Flex>
                                )}
                            </>
                        )}
                    </CardBody>
                </Card>

                {/* Create Client Modal */}
                <Modal isOpen={isCreateOpen} onClose={onCreateClose} size="lg">
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>Create New Client</ModalHeader>
                        <ModalCloseButton />
                        <ModalBody>
                            <VStack spacing={4}>
                                <FormControl isRequired isInvalid={!!formErrors.clientName}>
                                    <FormLabel>Client Name</FormLabel>
                                    <Input
                                        value={formData.clientName}
                                        onChange={(e) => setFormData(prev => ({ ...prev, clientName: e.target.value }))}
                                        placeholder="Enter client name (1-255 characters)"
                                        maxLength={255}
                                    />
                                    <FormErrorMessage>{formErrors.clientName}</FormErrorMessage>
                                </FormControl>
                                
                                <FormControl isRequired isInvalid={!!formErrors.clientSecret}>
                                    <FormLabel>Client Secret</FormLabel>
                                    <Input
                                        type="password"
                                        value={formData.clientSecret}
                                        onChange={(e) => setFormData(prev => ({ ...prev, clientSecret: e.target.value }))}
                                        placeholder="Enter client secret (8-1000 characters)"
                                        maxLength={1000}
                                    />
                                    <FormErrorMessage>{formErrors.clientSecret}</FormErrorMessage>
                                </FormControl>
                                <FormControl isRequired isInvalid={!!formErrors.prismVersion}>
                                    <FormLabel>Prism Version</FormLabel>
                                    <Input
                                        value={formData.prismVersion}
                                        onChange={(e) => setFormData(prev => ({ ...prev, prismVersion: e.target.value }))}
                                        placeholder="Enter Prism Version like 6.0 / 5.6.2 "
                                        maxLength={255}
                                    />
                                    <FormErrorMessage>{formErrors.prismVersion}</FormErrorMessage>
                                </FormControl>
                                <FormControl  isInvalid={!!formErrors.clientReportPerson}>
                                    <FormLabel>Report Person</FormLabel>
                                    <Input
                                        value={formData.clientReportPerson}
                                        onChange={(e) => setFormData(prev => ({ ...prev, clientReportPerson: e.target.value }))}
                                        placeholder="Enter client Report Person Name/Email"
                                        maxLength={1000}
                                    />
                                    <FormErrorMessage>{formErrors.clientReportPerson}</FormErrorMessage>
                                </FormControl>
                                <FormControl isInvalid={!!formErrors.clientStatus}>
                                    <FormLabel>Initial Status</FormLabel>
                                    <Select
                                        value={formData.clientStatus}
                                        onChange={(e) => setFormData(prev => ({ ...prev, clientStatus: e.target.value }))}
                                    >
                                        <option value="Offline">Offline</option>
                                        <option value="Online">Online</option>
                                        <option value="Maintenance">Maintenance</option>
                                    </Select>
                                    <FormErrorMessage>{formErrors.clientStatus}</FormErrorMessage>
                                </FormControl>
                                <FormControl >
                                    <FormLabel>Client Base Url</FormLabel>
                                        <Input
                                            value={formData.baseUrl}
                                            onChange={(e) => setFormData(prev => ({ ...prev, baseUrl: e.target.value }))}
                                            placeholder="https://edgedev.datazoic.com"
                                            maxLength={1000}
                                        />
                                </FormControl>
                                <FormControl>
                                    <FormLabel>Send Alerts for Checklist Results</FormLabel>
                                    <HStack spacing={3} mt={1}>
                                        <Switch
                                            isChecked={formData.sendChecklistAlerts}
                                            onChange={(e) => setFormData(prev => ({ ...prev, sendChecklistAlerts: e.target.checked }))}
                                            colorScheme="brand"
                                        />
                                        <Text fontSize="sm" color={textColor}>
                                            {formData.sendChecklistAlerts ? 'Alerts Enabled' : 'Alerts Disabled'}
                                        </Text>
                                    </HStack>
                                </FormControl>
                                <FormControl>
                                    <FormLabel>EMT Delay Alert</FormLabel>
                                    <HStack spacing={3} mt={1}>
                                        <Switch
                                            isChecked={formData.emtDelayEnabled}
                                            onChange={(e) => setFormData(prev => ({ ...prev, emtDelayEnabled: e.target.checked }))}
                                            colorScheme="brand"
                                        />
                                        <Text fontSize="sm" color={textColor}>
                                            {formData.emtDelayEnabled ? 'EMT Delay Alert Enabled' : 'EMT Delay Alert Disabled'}
                                        </Text>
                                    </HStack>
                                </FormControl>
                                <FormControl>
                                    <FormLabel>Internal Client</FormLabel>
                                    <HStack spacing={3} mt={1}>
                                        <Switch
                                            isChecked={formData.isInternal}
                                            onChange={(e) => setFormData(prev => ({ ...prev, isInternal: e.target.checked }))}
                                            colorScheme="brand"
                                        />
                                        <Text fontSize="sm" color={textColor}>
                                            {formData.isInternal ? 'Internal' : 'External'}
                                        </Text>
                                    </HStack>
                                </FormControl>
                                <FormControl>
                                    <FormLabel>Redmine Project ID</FormLabel>
                                    <Input
                                        value={formData.redmineProjectId}
                                        onChange={(e) => setFormData(prev => ({ ...prev, redmineProjectId: e.target.value }))}
                                        placeholder="e.g. ask-donna (client's Redmine project identifier)"
                                        maxLength={200}
                                    />
                                    <Text fontSize="xs" color={textColor} mt={1}>
                                        Used to create Redmine tickets from this client's issues.
                                    </Text>
                                </FormControl>
                                <FormControl>
                                    <FormLabel>Market Data Client Name 1</FormLabel>
                                    <Input
                                        value={formData.marketDataClient1}
                                        onChange={(e) => setFormData(prev => ({ ...prev, marketDataClient1: e.target.value }))}
                                        placeholder="Enter Market Data Client Name 1"
                                        maxLength={255}
                                    />
                                </FormControl>

                                <FormControl>
                                    <FormLabel>Market Data Client Name 2</FormLabel>
                                    <Input
                                        value={formData.marketDataClient2}
                                        onChange={(e) => setFormData(prev => ({ ...prev, marketDataClient2: e.target.value }))}
                                        placeholder="Enter Market Data Client Name 2"
                                        maxLength={255}
                                    />
                                </FormControl>
                            </VStack>
                        </ModalBody>
                        <ModalFooter>
                            <Button variant="ghost" mr={3} onClick={onCreateClose}>
                                Cancel
                            </Button>
                            <Button 
                                colorScheme="brand" 
                                onClick={handleCreateClient}
                                isLoading={operationLoading}
                                loadingText="Creating..."
                            >
                                Create Client
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>

                {/* Edit Client Modal */}
                <Modal isOpen={isEditOpen} onClose={onEditClose} size="lg">
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>Edit Client</ModalHeader>
                        <ModalCloseButton />
                        <ModalBody>
                            <VStack spacing={4}>
                                <FormControl isRequired isInvalid={!!formErrors.clientName}>
                                    <FormLabel>Client Name</FormLabel>
                                    <Input
                                        value={formData.clientName}
                                        onChange={(e) => setFormData(prev => ({ ...prev, clientName: e.target.value }))}
                                        placeholder="Enter client name"
                                        maxLength={255}
                                    />
                                    <FormErrorMessage>{formErrors.clientName}</FormErrorMessage>
                                </FormControl>
                                
                                <FormControl isInvalid={!!formErrors.clientSecret}>
                                    <FormLabel>Client Secret (Optional)</FormLabel>
                                    <Input
                                        type="password"
                                        value={formData.clientSecret}
                                        onChange={(e) => setFormData(prev => ({ ...prev, clientSecret: e.target.value }))}
                                        placeholder="Leave empty to keep current secret"
                                        maxLength={1000}
                                    />
                                    <FormErrorMessage>{formErrors.clientSecret}</FormErrorMessage>
                                </FormControl>

                                <FormControl isRequired isInvalid={!!formErrors.prismVersion}>
                                    <FormLabel>Prism Version</FormLabel>
                                    <Input
                                        value={formData.prismVersion}
                                        onChange={(e) => setFormData(prev => ({ ...prev, prismVersion: e.target.value }))}
                                        placeholder="Enter Prism Version like 6.0 / V5.6.2 "
                                        maxLength={255}
                                    />
                                    <FormErrorMessage>{formErrors.prismVersion}</FormErrorMessage>
                                </FormControl>
                                <FormControl isInvalid={!!formErrors.clientReportPerson}>
                                    <FormLabel>Report Person</FormLabel>
                                    <Input
                                        value={formData.clientReportPerson}
                                        onChange={(e) => setFormData(prev => ({ ...prev, clientReportPerson: e.target.value }))}
                                        placeholder="Enter client Report Person Name/Email"
                                        maxLength={1000}
                                    />
                                    <FormErrorMessage>{formErrors.clientReportPerson}</FormErrorMessage>
                                </FormControl>
                                <FormControl isInvalid={!!formErrors.clientStatus}>
                                    <FormLabel>Status</FormLabel>
                                    <Select
                                        value={formData.clientStatus}
                                        onChange={(e) => setFormData(prev => ({ ...prev, clientStatus: e.target.value }))}
                                    >
                                        <option value="Online">Online</option>
                                        <option value="Offline">Offline</option>
                                        <option value="Maintenance">Maintenance</option>
                                    </Select>
                                    <FormErrorMessage>{formErrors.clientStatus}</FormErrorMessage>
                                </FormControl>
                                <FormControl >
                                <FormLabel>Client Base Url</FormLabel>
                                    <Input
                                        value={formData.baseUrl}
                                        onChange={(e) => setFormData(prev => ({ ...prev, baseUrl: e.target.value }))}
                                        placeholder="https://edgedev.datazoic.com"
                                        maxLength={1000}
                                    />
                                </FormControl>
                                <FormControl>
                                    <FormLabel>Send Alerts for Checklist Results</FormLabel>
                                    <HStack spacing={3} mt={1}>
                                        <Switch
                                            isChecked={formData.sendChecklistAlerts}
                                            onChange={(e) => setFormData(prev => ({ ...prev, sendChecklistAlerts: e.target.checked }))}
                                            colorScheme="brand"
                                        />
                                        <Text fontSize="sm" color={textColor}>
                                            {formData.sendChecklistAlerts ? 'Alerts Enabled' : 'Alerts Disabled'}
                                        </Text>
                                    </HStack>
                                </FormControl>
                                <FormControl>
                                    <FormLabel>EMT Delay Alert</FormLabel>
                                    <HStack spacing={3} mt={1}>
                                        <Switch
                                            isChecked={formData.emtDelayEnabled}
                                            onChange={(e) => setFormData(prev => ({ ...prev, emtDelayEnabled: e.target.checked }))}
                                            colorScheme="brand"
                                        />
                                        <Text fontSize="sm" color={textColor}>
                                            {formData.emtDelayEnabled ? 'EMT Delay Alert Enabled' : 'EMT Delay Alert Disabled'}
                                        </Text>
                                    </HStack>
                                </FormControl>
                                <FormControl>
                                    <FormLabel>Internal Client</FormLabel>
                                    <HStack spacing={3} mt={1}>
                                        <Switch
                                            isChecked={formData.isInternal}
                                            onChange={(e) => setFormData(prev => ({ ...prev, isInternal: e.target.checked }))}
                                            colorScheme="brand"
                                        />
                                        <Text fontSize="sm" color={textColor}>
                                            {formData.isInternal ? 'Internal' : 'External'}
                                        </Text>
                                    </HStack>
                                </FormControl>
                                <FormControl>
                                    <FormLabel>Redmine Project ID</FormLabel>
                                    <Input
                                        value={formData.redmineProjectId}
                                        onChange={(e) => setFormData(prev => ({ ...prev, redmineProjectId: e.target.value }))}
                                        placeholder="e.g. ask-donna (client's Redmine project identifier)"
                                        maxLength={200}
                                    />
                                    <Text fontSize="xs" color={textColor} mt={1}>
                                        Used to create Redmine tickets from this client's issues.
                                    </Text>
                                </FormControl>
                                <FormControl>
                                    <FormLabel>Market Data Client Name 1</FormLabel>
                                    <Input
                                        value={formData.marketDataClient1}
                                        onChange={(e) => setFormData(prev => ({ ...prev, marketDataClient1: e.target.value }))}
                                        placeholder="Enter Market Data Client Name 1"
                                        maxLength={255}
                                    />
                                </FormControl>

                                <FormControl>
                                    <FormLabel>Market Data Client Name 2</FormLabel>
                                    <Input
                                        value={formData.marketDataClient2}
                                        onChange={(e) => setFormData(prev => ({ ...prev, marketDataClient2: e.target.value }))}
                                        placeholder="Enter Market Data Client Name 2"
                                        maxLength={255}
                                    />
                                </FormControl>

                                {currentClient && (
                                    <Box p={3} bg={statsBg} borderRadius="md" w="full">
                                        <Text fontSize="sm" fontWeight="semibold" mb={2}>Client Information:</Text>
                                        <VStack align="start" spacing={1} fontSize="xs">
                                            <Text><strong>ID:</strong> {currentClient.clientId}</Text>
                                            <Text><strong>Code:</strong> {currentClient.clientCode}</Text>
                                            <Text><strong>Created:</strong> {formatDateTime(currentClient.createdAtUtc)}</Text>
                                            <Text><strong>Last Updated:</strong> {formatDateTime(currentClient.updatedAtUtc)}</Text>
                                        </VStack>
                                    </Box>
                                )}
                            </VStack>
                        </ModalBody>
                        <ModalFooter>
                            <Button variant="ghost" mr={3} onClick={onEditClose}>
                                Cancel
                            </Button>
                            <Button 
                                colorScheme="brand" 
                                onClick={handleUpdateClient}
                                isLoading={operationLoading}
                                loadingText="Updating..."
                            >
                                Update Client
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>

                {/* Delete Confirmation Modal */}
                <Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>Confirm Deletion</ModalHeader>
                        <ModalCloseButton />
                        <ModalBody>
                            <Alert status="warning" borderRadius="md">
                                <AlertIcon />
                                <VStack align="start" spacing={2} ml={2}>
                                    <Text fontWeight="semibold">
                                        Are you sure you want to delete the client "{currentClient?.clientName}"?
                                    </Text>
                                    <Text fontSize="sm">
                                        This action cannot be undone. The client will be permanently deleted from the system.
                                    </Text>
                                    {currentClient && (
                                        <Box fontSize="xs" color={textColor} mt={2}>
                                            <Text><strong>Client ID:</strong> {currentClient.clientId}</Text>
                                            <Text><strong>Status:</strong> {currentClient.clientStatus}</Text>
                                        </Box>
                                    )}
                                </VStack>
                            </Alert>
                        </ModalBody>
                        <ModalFooter>
                            <Button variant="ghost" mr={3} onClick={onDeleteClose}>
                                Cancel
                            </Button>
                            <Button 
                                colorScheme="red" 
                                onClick={handleDeleteClient}
                                isLoading={operationLoading}
                                loadingText="Deleting..."
                            >
                                Delete Client
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>

                {/* Batch Operations Modal */}
                <Modal isOpen={isBatchOpen} onClose={onBatchClose} size="lg">
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>Batch Operations</ModalHeader>
                        <ModalCloseButton />
                        <ModalBody>
                            <VStack spacing={4}>
                                <Alert status="info" borderRadius="md">
                                    <AlertIcon />
                                    <Text>
                                        You have selected {selectedClients.length} client{selectedClients.length !== 1 ? 's' : ''} for batch operation.
                                    </Text>
                                </Alert>
                                
                                <FormControl>
                                    <FormLabel>Operation Type</FormLabel>
                                    <Select
                                        value={batchOperation.operation}
                                        onChange={(e) => setBatchOperation(prev => ({ ...prev, operation: e.target.value }))}
                                    >
                                        <option value="status">Update Status</option>
                                        <option value="delete">Delete Clients</option>
                                    </Select>
                                </FormControl>
                                
                                {batchOperation.operation === 'status' && (
                                    <FormControl>
                                        <FormLabel>New Status</FormLabel>
                                        <Select
                                            value={batchOperation.status}
                                            onChange={(e) => setBatchOperation(prev => ({ ...prev, status: e.target.value }))}
                                        >
                                            <option value="Online">Online</option>
                                            <option value="Offline">Offline</option>
                                            <option value="Maintenance">Maintenance</option>
                                        </Select>
                                    </FormControl>
                                )}
                                
                                {batchOperation.operation === 'delete' && (
                                    <Alert status="error" borderRadius="md">
                                        <AlertIcon />
                                        <Text>
                                            <strong>Warning:</strong> This will permanently delete all selected clients. This action cannot be undone.
                                        </Text>
                                    </Alert>
                                )}
                            </VStack>
                        </ModalBody>
                        <ModalFooter>
                            <Button variant="ghost" mr={3} onClick={onBatchClose}>
                                Cancel
                            </Button>
                            <Button 
                                colorScheme={batchOperation.operation === 'delete' ? 'red' : 'brand'}
                                onClick={handleBatchOperation}
                                isLoading={operationLoading}
                                loadingText="Processing..."
                            >
                                {batchOperation.operation === 'delete' ? 'Delete Clients' : 'Update Status'}
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>

                {/* Statistics Modal */}
                <Modal isOpen={isStatsOpen} onClose={onStatsClose} size="xl">
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>Client Statistics</ModalHeader>
                        <ModalCloseButton />
                        <ModalBody>
                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                {statistics.map((stat, index) => (
                                    <Box key={index} p={4} bg={statsBg} borderRadius="md">
                                        <Stat>
                                            <StatLabel fontSize="md" color={textColor}>
                                                {stat.label || stat.name}
                                            </StatLabel>
                                            <StatNumber fontSize="3xl" color={headingColor}>
                                                {formatNumber(stat.value || stat.count)}
                                            </StatNumber>
                                            {!(stat.key === 'total' || (stat.label || stat.name).toLowerCase().includes('total')) && stat.percentage && (
                                                <StatHelpText>
                                                    <StatArrow type={stat.percentage > 0 ? 'increase' : 'decrease'} />
                                                    {Math.abs(stat.percentage)}% of total
                                                </StatHelpText>
                                            )}
                                            {stat.description && (
                                                <Text fontSize="sm" color={textColor} mt={2}>
                                                    {stat.description}
                                                </Text>
                                            )}
                                        </Stat>
                                    </Box>
                                ))}
                            </SimpleGrid>
                        </ModalBody>
                        <ModalFooter>
                            <Button onClick={onStatsClose}>Close</Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>
            </VStack>
        </Box>
    );
}

export default Client;